// Project schemas — plan §4.3.4 + §11.2 + §11.7 (M3 S3: framework_version_id FK + snapshot)
import { z } from "zod";
import { PARTICIPANT_STATUSES } from "../constants/index.js";
import { PeriodType } from "./period.js";

export const projectStatusEnum = z.enum(["draft", "in_progress", "completed"]);
export type ProjectStatus = z.infer<typeof projectStatusEnum>;

export const ProjectSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  clientName: z.string(),
  frameworkVersionId: z.string().uuid(),
  // TODO-KPI-12: sürümden bağımsız çerçeve kimliği — ProjectDetail KPI seti seçicisi
  // "(uyumlu)" sıralamasını (sortKpiSetsForFramework) bununla yapar; join zaten var.
  frameworkId: z.string().uuid(),
  frameworkName: z.string(),
  versionNumber: z.string(),
  reportingYear: z.number().int(),
  // Raporlama dönemi (K5): reportingYear yanında annual/q1/q2/q3. DB default "annual".
  periodType: PeriodType,
  status: projectStatusEnum,
  assignedToId: z.string().uuid(),
  assignedToName: z.string(),
  createdById: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  // Geçmiş Rapor Aktarımı (Task 1 regresyon/tasarım review): proje en az bir
  // report_import kaydından türediyse true. Zorunlu (default YOK) — her tüketici
  // (BE toProject, FE liste/detay) açıkça doldurmalı.
  hasImport: z.boolean(),
  // TODO-KPI-12: projeye bağlı KPI seti (kpi_sets LEFT JOIN). null = raporda KPI tablosu yok.
  // Kurulumdan sonra PATCH /projects/:id { kpiSetId } ile değiştirilir; snapshot etkilenmez.
  kpiSetId: z.string().uuid().nullable(),
  kpiSetName: z.string().nullable(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const ProjectsListResponseSchema = z.object({
  projects: z.array(ProjectSchema),
  metrics: z.object({
    total: z.number().int(),
    draft: z.number().int(),
    inProgress: z.number().int(),
    completed: z.number().int(),
  }),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type ProjectsListResponse = z.infer<typeof ProjectsListResponseSchema>;

// §4.3 liste filtreleri
export const ProjectsListQuerySchema = z.object({
  search: z.string().optional(),
  clientId: z.string().uuid().optional(),
  frameworkVersionId: z.string().uuid().optional(),
  // Geçmiş Rapor Aktarımı (Task 13 fix round 1): "zaten var" ön kontrolü sunucudaki
  // tekillik kimliğiyle AYNI olmalı — `report_imports` tekilliği framework_id bazlı,
  // versiyon bazlı DEĞİL. `frameworkVersions` zaten JOIN'li, ek alt sorgu gerekmez.
  frameworkId: z.string().uuid().optional(),
  reportingYear: z.coerce.number().int().min(2000).max(2100).optional(),
  periodType: PeriodType.optional(),
  status: projectStatusEnum.optional(),
  assignedTo: z.string().uuid().optional(),
  // Geçmiş Rapor Aktarımı (tasarım review 4A): import'tan doğan vs. elle oluşturulan proje filtresi.
  source: z.enum(["import", "manual"]).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(25),
});
export type ProjectsListQuery = z.infer<typeof ProjectsListQuerySchema>;

export const CreateProjectRequestSchema = z.object({
  clientId: z.string().uuid(),
  frameworkVersionId: z.string().uuid(),
  // M13: opsiyonel sektör paketi. Backend doğrular: paket.parentFrameworkVersionId
  // === frameworkVersionId. Snapshot resolved view ile yazılır.
  sectorPackageId: z.string().uuid().optional(),
  // M16 KPI (K28): opsiyonel gösterge seti. Backend doğrular: set.clientId === clientId
  // (aksi 422 kpi_set_mismatch). NULL = raporda KPI tablosu yok.
  kpiSetId: z.string().uuid().optional(),
  reportingYear: z.number().int().min(2000).max(2100),
  periodType: PeriodType.default("annual"),
  assignedTo: z.string().uuid().optional(), // boşsa kendi
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;

export const UpdateProjectRequestSchema = z
  .object({
    reportingYear: z.number().int().min(2000).max(2100).optional(),
    status: projectStatusEnum.optional(),
    assignedTo: z.string().uuid().optional(),
    // TODO-KPI-12: KPI seti bağla/değiştir (uuid) ya da bağı kaldır (null). Set projenin
    // müşterisine ait olmalı (422 kpi_set_mismatch); tamamlanmış projede 409 project_completed.
    kpiSetId: z.string().uuid().nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "En az bir alan güncellenmeli" });
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>;

// Client (müşteri) aktöre dar proje görünümü — Müşteri Katılımcı Rolü Task 5,
// eng-review D15: listProjects/getProject/snapshot proje meta'sı client aktöre
// SADECE bu alanları döner — assignedToName/iç durum/maliyet client payload'ına
// hiç girmez ("müşteri iç veriyi görmez" iddiası uçtan uca tutarlı).
export const ClientProjectViewSchema = z.object({
  id: z.string().uuid(),
  // 2026-09-30 — aynı kişi birden fazla müşteri firmaya davet edilebildiği için
  // (client_mismatch kısıtı kalktı) kartta/başlıkta hangi şirket olduğu yazılır.
  clientName: z.string(),
  // K8 (2026-10-23) — çok firmalı müşteri portalda firma seçer; liste aktif firmaya süzülür.
  clientId: z.string().uuid(),
  frameworkName: z.string(),
  reportingYear: z.number().int(),
  periodType: PeriodType,
  participantStatus: z.enum(PARTICIPANT_STATUSES),
});
export type ClientProjectView = z.infer<typeof ClientProjectViewSchema>;

export const ClientProjectsListResponseSchema = z.object({
  projects: z.array(ClientProjectViewSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type ClientProjectsListResponse = z.infer<typeof ClientProjectsListResponseSchema>;
